import { Box, Tooltip, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { useSelector } from "react-redux";
import React, { useEffect, useMemo, useState } from "react";
import {
  getPermitTypeData,
  isCurrentFormNOC,
} from "../../permitForms/permitFormHelpers";

const PermitPackageIndicators = () => {
  const { selectedRow, leftValue, selectedPermitType } = useSelector(
    (state) => state.myPermitsComponent,
  );
  const [signOnly, setSignOnly] = useState(true);

  useEffect(() => {
    const checkSignOnly = () => {
      // Check if all documents are sign-only (only sign: true, all other properties false)
      if (
        !selectedRow?.selected_documents ||
        selectedRow.selected_documents.length === 0
      ) {
        return;
      }

      const document = selectedRow?.selected_documents?.[leftValue];
      if (!document) {
        return false;
      }

      let isSignOnly = true;

      // Iterate through all selected documents

      const signatures = document?.signatures;

      if (!signatures) {
        return false;
      }

      // Iterate through all signature parties (owner, contractor, private_provider, examiner, etc.)
      for (const [partyName, partyData] of Object.entries(signatures)) {
        // Skip 'order' and other non-party properties
        if (
          partyName === "order" ||
          typeof partyData !== "object" ||
          partyData === null
        ) {
          continue;
        }

        // Only check parties that have sign: true
        // If sign is false, we don't care about other properties
        if (partyData.sign !== true) {
          continue;
        }

        // For parties with sign: true, check all other properties (notary, etc.)
        // They should all be false for it to be sign-only
        const otherProperties = Object.entries(partyData).filter(
          ([key]) => key !== "sign",
        );

        const hasOtherTrueValues = otherProperties.some(
          ([, value]) => value === true,
        );

        // If sign is true but any other property is also true, it's not sign-only
        if (hasOtherTrueValues) {
          isSignOnly = false;
          break;
        }
      }

      // If we found a document that's not sign-only, break early
      if (!isSignOnly) {
        return false;
      }

      return isSignOnly;
    };

    const signatureOnly = checkSignOnly();
    setSignOnly(signatureOnly);
  }, [selectedRow, leftValue]);

  const currentPermitData = useMemo(
    () => getPermitTypeData(selectedRow, selectedPermitType),
    [selectedRow, selectedPermitType],
  );
  const isMainPermitType = selectedPermitType === selectedRow?.permit_type;

  // Check if the selected document needs notarization or signing
  const needsNotarizationOrSigning = useMemo(() => {
    const document = selectedRow?.selected_documents?.[leftValue];
    if (!document) return false;

    const signatures = document?.signatures;
    if (!signatures) return false;

    // Check all signature parties to see if any require signing or notarization
    for (const [partyName, partyData] of Object.entries(signatures)) {
      // Skip 'order' and other non-party properties
      if (
        partyName === "order" ||
        typeof partyData !== "object" ||
        partyData === null
      ) {
        continue;
      }

      // If any party has sign: true or notary: true, the document needs action
      if (partyData.sign === true || partyData.notary === true) {
        return true;
      }
    }

    return false;
  }, [selectedRow, leftValue]);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 4,
        py: 1,
        px: 1,
        borderBottom: "1px solid #e0e0e0", // Add a subtle divider below
      }}
    >
      {/* Created Status */}
      <Tooltip title="Document Created">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {currentPermitData?.selected_documents?.[leftValue]?.created ? (
            <CheckCircleIcon color="success" sx={{ fontSize: 30 }} />
          ) : (
            <CancelIcon color="error" sx={{ fontSize: 30 }} />
          )}
          <Typography variant="caption" color="textSecondary" fontWeight="bold">
            Created
          </Typography>
        </Box>
      </Tooltip>
      {/* Notarized Status - Only show if document needs notarization or signing */}
      {needsNotarizationOrSigning && (
        <Tooltip title={signOnly ? "Document Signed" : "Document Notarized"}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {
              // Case 1: Current form is NOC and noc_notarized is true
              (isCurrentFormNOC(selectedRow, selectedPermitType, leftValue) &&
                selectedRow?.notarized === true &&
                selectedRow?.noc_created === true &&
                isMainPermitType) ||
              // Case 2: Current form is noc_affidavit, noc_notarized is true, and final_documents array includes the selected document's name
              (currentPermitData?.selected_documents?.[leftValue]?.name ===
                "noc_affidavit" &&
                selectedRow?.notarized === true &&
                selectedRow?.noc_created === true &&
                isMainPermitType &&
                selectedRow?.processing_permit?.notarization?.final_documents?.some(
                  (doc) =>
                    doc?.name ===
                    selectedRow?.selected_documents?.[leftValue]?.name,
                )) ||
              // Case 3: Current form is not NOC and pp_notarized is true
              (!isCurrentFormNOC(selectedRow, selectedPermitType, leftValue) &&
                selectedRow?.pp_notarized === true &&
                isMainPermitType) ? (
                <CheckCircleIcon color="success" sx={{ fontSize: 30 }} />
              ) : (
                // Default case: red cancel icon
                <CancelIcon color="error" sx={{ fontSize: 30 }} />
              )
            }
            <Typography
              variant="caption"
              color="textSecondary"
              fontWeight="bold"
            >
              {signOnly ? "Signed" : "Notarized"}
            </Typography>
          </Box>
        </Tooltip>
      )}
      {/* Recorded Status */}
      {selectedRow?.selected_documents?.[leftValue]?.name === "noc" &&
        isMainPermitType && (
          <Tooltip title="Document Recorded">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              {selectedRow?.recorded ? (
                <CheckCircleIcon color="success" sx={{ fontSize: 30 }} />
              ) : (
                <CancelIcon color="error" sx={{ fontSize: 30 }} />
              )}
              <Typography
                variant="caption"
                color="textSecondary"
                fontWeight="bold"
              >
                Recorded
              </Typography>
            </Box>
          </Tooltip>
        )}
      {/* Downloaded Status */}
      <Tooltip title="Document Downloaded">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {currentPermitData?.documents?.[
            currentPermitData?.selected_documents?.[leftValue]?.name
          ]?.download_info?.downloaded === true ? (
            <CheckCircleIcon color="success" sx={{ fontSize: 30 }} />
          ) : (
            <CancelIcon color="error" sx={{ fontSize: 30 }} />
          )}
          <Typography variant="caption" color="textSecondary" fontWeight="bold">
            Downloaded
          </Typography>
        </Box>
      </Tooltip>
    </Box>
  );
};

export default React.memo(PermitPackageIndicators);
